'use client';

import { Share2 } from 'lucide-react';
import { XIcon, FacebookIcon, LinkedInIcon } from '@/components/icons/SocialIcons';
import { getCurrentUrl } from '@/lib/url-utils';
import { trackShare } from '@/lib/analytics';

interface ShareButtonsProps {
  title: string;
  url?: string;
  contentType?: 'article' | 'coin' | 'nft';
}

export default function ShareButtons({ title, url, contentType = 'article' }: ShareButtonsProps) {
  const shareUrl = url || getCurrentUrl();

  const handleTwitterShare = () => {
    const text = `Check out this article: ${title}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    
    // Track the share event
    trackShare({
      platform: 'twitter',
      contentType,
      contentTitle: title,
      contentUrl: shareUrl,
    });
  };

  const handleFacebookShare = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    
    // Track the share event
    trackShare({
      platform: 'other',
      contentType,
      contentTitle: title,
      contentUrl: shareUrl,
    });
  };

  const handleLinkedInShare = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
    
    // Track the share event
    trackShare({
      platform: 'other',
      contentType,
      contentTitle: title,
      contentUrl: shareUrl,
    });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      
      // Track the copy event
      trackShare({
        platform: 'copy_link',
        contentType,
        contentTitle: title,
        contentUrl: shareUrl,
      });
      // You could add a toast notification here
      } catch (err) {
      }
  };

  return (
    <div className="flex items-center gap-4 pb-6 border-b">
      <span className="text-sm font-medium text-gray-700">Share:</span>
      <div className="flex items-center gap-2">
        <button
          onClick={handleTwitterShare}
          className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-gray-500 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
          title="Share on X"
          aria-label="Share on X"
        >
          <XIcon className="w-5 h-5" />
        </button>
        <button
          onClick={handleFacebookShare}
          className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
          title="Share on Facebook"
          aria-label="Share on Facebook"
        >
          <FacebookIcon className="w-5 h-5" />
        </button>
        <button
          onClick={handleLinkedInShare}
          className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-gray-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
          title="Share on LinkedIn"
          aria-label="Share on LinkedIn"
        >
          <LinkedInIcon className="w-5 h-5" />
        </button>
        <button
          onClick={handleCopyLink}
          className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-gray-500 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors"
          title="Copy link"
          aria-label="Copy link"
        >
          <Share2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

